import React, { lazy, Suspense } from 'react';

import type { ComponentType } from 'react';

export interface LazyWithSuspenseOptions {
  // Fallback component to show while loading (optional)
  fallback?: React.ReactNode;

  // This option is accepted but ignored since React 18 handles SSR natively
  ssr?: boolean;
}

/**
 * Higher-Order Component that combines React.lazy with Suspense
 *
 * @example
 * ```tsx
 * const MyPage = lazyWithSuspense(() => import('./pages/MyPage'));
 * const MyPage2 = lazyWithSuspense(() => import('./pages/MyPage2'), { fallback: <div>Loading...</div> });
 *
 * // Usage:
 * <MyPage />
 * <MyPage2 />
 * ```
 *
 * @param importFn - Dynamic import function that returns a Promise with a default export
 * @param options - Optional configuration (accepted for API compatibility but currently ignored)
 * @returns A component wrapped with lazy and Suspense
 */
export function lazyWithSuspense<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options?: LazyWithSuspenseOptions,
): ComponentType<React.ComponentProps<T>> {
  const LazyComponent = lazy(importFn);
  const fallback = options?.fallback || null;

  return (props: React.ComponentProps<T>) => {
    const { key: _key, ...restProps } = props as React.ComponentProps<T> & { key?: React.Key };

    return (
      <Suspense fallback={fallback}>
        <LazyComponent {...(restProps as React.ComponentProps<T>)} />
      </Suspense>
    );
  };
}
